import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Plus, Bot, Pencil, Archive, BarChart3, Zap, Trash2 } from 'lucide-react';
import type { Agent } from '@conversia/shared-types';
import * as agentsApi from '@/api/agents.api';
import * as consumosApi from '@/api/consumos.api';
import { getAssistants, type AssistantStatus } from '@/api/assistants.api';
import * as installationApi from '@/api/installation.api';
import { AgentReportModal } from '@/components/agent/AgentReportModal';
import { relativeTime } from '@/lib/format';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { Modal } from '@/components/ui/Modal';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { PageHeader } from '@/components/common/PageHeader';
import { AgentFormModal } from '@/components/agent/AgentFormModal';
import { agentStatusBadge } from '@/lib/badges';
import { useToast } from '@/hooks/useToast';

export default function Agents() {
  const qc = useQueryClient();
  const toast = useToast();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Agent | null>(null);
  const [reporting, setReporting] = useState<Agent | null>(null);
  const [deleting, setDeleting] = useState<Agent | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['agents', 'list'],
    queryFn: () => agentsApi.listAgents({ take: 100 }),
  });

  // Nombre llano de cada modelo del catálogo + costo del mes por agente
  // (reusa el resumen de consumos existente; nada nuevo en el server).
  const { data: modelCatalog } = useQuery({
    queryKey: ['agents', 'models'],
    queryFn: agentsApi.getModelCatalog,
  });
  const consumoMes = useQuery({
    queryKey: ['consumos', 'summary', 'mes'],
    queryFn: () => consumosApi.getSummary('mes'),
  });
  const modelName = (id: string | null | undefined) =>
    id ? (modelCatalog?.modelos.find((m) => m.id === id)?.nombre ?? id) : 'Default instalación';
  const costoMes = (agentId: string) =>
    consumoMes.data?.porAgente.find((a) => a.agentId === agentId)?.usd ?? 0;

  const archive = useMutation({
    mutationFn: (id: string) => agentsApi.archiveAgent(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['agents'] });
      toast.success('Agente archivado');
    },
    onError: () => toast.error('No se pudo archivar el agente'),
  });

  // Borrado REAL con guardas del server (default de canal / conversaciones
  // abiertas → 422 con la explicación; se muestra tal cual).
  const remove = useMutation({
    mutationFn: (id: string) => agentsApi.deleteAgent(id),
    onSuccess: () => {
      setDeleting(null);
      void qc.invalidateQueries({ queryKey: ['agents'] });
      toast.success('Agente borrado');
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : 'No se pudo borrar el agente'),
  });

  const openNew = () => {
    setEditing(null);
    setModalOpen(true);
  };
  const openEdit = (agent: Agent) => {
    setEditing(agent);
    setModalOpen(true);
  };

  const agents = data?.items ?? [];

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Agentes AI"
        helpKey="agents"
        description="Equipos de agentes que responden automáticamente en tus canales."
        action={
          <Button onClick={openNew}>
            <Plus className="h-4 w-4" /> Nuevo agente
          </Button>
        }
      />

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner size={28} />
        </div>
      ) : agents.length === 0 ? (
        <EmptyState
          icon={Bot}
          title="Aún no tienes agentes"
          description="Crea tu primer agente AI y define su system prompt para empezar a responder."
          action={
            <Button onClick={openNew}>
              <Plus className="h-4 w-4" /> Crear agente
            </Button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {agents.map((agent) => {
            const badge = agentStatusBadge(agent.status);
            return (
              <Card key={agent.id} className="flex flex-col">
                <CardBody className="flex flex-1 flex-col gap-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300">
                        <Bot className="h-5 w-5" />
                      </span>
                      <div className="min-w-0">
                        <h3 className="truncate font-semibold text-slate-900 dark:text-slate-100">
                          {agent.name}
                        </h3>
                        <Badge variant={badge.variant}>{badge.label}</Badge>
                      </div>
                    </div>
                  </div>
                  <p className="line-clamp-3 flex-1 text-sm text-slate-500 dark:text-slate-400">
                    {agent.description || 'Sin descripción.'}
                  </p>
                  <div className="flex flex-wrap items-center gap-1.5 text-xs">
                    <Badge variant={agent.engineType === 'cli' ? 'info' : 'brand'}>
                      {agent.engineType === 'cli' ? 'Motor CLI (Max)' : 'Motor API'}
                    </Badge>
                    <Badge variant="default">{modelName(agent.model)}</Badge>
                    <span className="ml-auto text-slate-400">
                      Este mes:{' '}
                      <span className="font-medium text-slate-600 dark:text-slate-300">
                        {agent.engineType === 'cli'
                          ? `${consumoMes.data?.porAgente.find((a) => a.agentId === agent.id)?.usosCli ?? 0} usos`
                          : `US$${costoMes(agent.id).toFixed(2)}`}
                      </span>
                    </span>
                  </div>
                  <div className="flex items-center gap-2 border-t border-surface-border pt-3 dark:border-slate-800">
                    <Button variant="outline" size="sm" onClick={() => openEdit(agent)}>
                      <Pencil className="h-4 w-4" /> Editar
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setReporting(agent)}>
                      <BarChart3 className="h-4 w-4" /> Qué hizo
                    </Button>
                    {agent.status !== 'ARCHIVED' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => archive.mutate(agent.id)}
                        loading={archive.isPending && archive.variables === agent.id}
                      >
                        <Archive className="h-4 w-4" /> Archivar
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="ml-auto text-danger hover:bg-red-50 hover:text-danger dark:hover:bg-red-900/20"
                      onClick={() => setDeleting(agent)}
                    >
                      <Trash2 className="h-4 w-4" /> Borrar
                    </Button>
                  </div>
                </CardBody>
              </Card>
            );
          })}
        </div>
      )}

      <AsistentesSection />

      <AgentFormModal open={modalOpen} onClose={() => setModalOpen(false)} agent={editing} />
      <AgentReportModal agent={reporting} onClose={() => setReporting(null)} />
      <Modal
        open={Boolean(deleting)}
        onClose={() => setDeleting(null)}
        title={`Borrar "${deleting?.name ?? ''}"`}
        description="Esto borra el agente de verdad (no es archivar). Sus conversaciones cerradas y el histórico de consumos se conservan."
        footer={
          <>
            <Button variant="outline" onClick={() => setDeleting(null)}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              onClick={() => deleting && remove.mutate(deleting.id)}
              loading={remove.isPending}
            >
              Sí, borrar {deleting?.name}
            </Button>
          </>
        }
      >
        <p className="text-sm text-slate-600 dark:text-slate-300">
          Si el agente es el agente por defecto de un canal o tiene conversaciones abiertas, el
          sistema lo va a impedir y te va a explicar por qué (en ese caso puedes archivarlo).
        </p>
      </Modal>
    </div>
  );
}

/**
 * Asistentes automáticos: los "agentes escondidos" — plantillas de los
 * workers/ponches, NO IA (y así siguen). Solo se hacen visibles: qué son,
 * su actividad de hoy y dónde se edita su plantilla.
 */
function AsistentesSection() {
  const [editingPlantilla, setEditingPlantilla] = useState<AssistantStatus | null>(null);
  const { data, isLoading } = useQuery({
    queryKey: ['assistants', 'hoy'],
    queryFn: () => getAssistants(),
    refetchInterval: 60_000,
  });

  const estadoBadge = (estado: string) =>
    estado === 'activo'
      ? { variant: 'success' as const, label: 'Activo' }
      : estado === 'worker_caido'
        ? { variant: 'danger' as const, label: 'Proceso caído' }
        : { variant: 'default' as const, label: 'Sin plantilla' };

  return (
    <section className="space-y-3">
      <div>
        <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-900 dark:text-slate-100">
          <Zap className="h-5 w-5 text-amber-500" /> Asistentes automáticos
        </h2>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Avisos que salen solos con plantillas (no usan IA). Sus textos se editan en
          Instalación → Mensajes.
        </p>
      </div>
      {isLoading ? (
        <Spinner size={22} />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {(data ?? []).map((a) => {
            const badge = estadoBadge(a.estado);
            return (
              <Card key={a.key}>
                <CardBody className="space-y-2">
                  <div className="flex items-center gap-2">
                    <p className="min-w-0 flex-1 truncate font-medium text-slate-800 dark:text-slate-100">
                      {a.nombre}
                    </p>
                    <Badge variant={badge.variant}>{badge.label}</Badge>
                  </div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{a.queHace}</p>
                  <p className="text-xs text-slate-400">
                    Hoy: {a.enviados} enviado{a.enviados === 1 ? '' : 's'} · Último:{' '}
                    {a.ultimoAt ? relativeTime(a.ultimoAt) : 'nunca'}
                  </p>
                  <button
                    type="button"
                    onClick={() => setEditingPlantilla(a)}
                    className="inline-flex items-center gap-1 text-xs text-brand-600 hover:underline dark:text-brand-400"
                  >
                    <Pencil className="h-3 w-3" /> Editar plantilla
                  </button>
                </CardBody>
              </Card>
            );
          })}
        </div>
      )}
      <PlantillaEditorModal
        asistente={editingPlantilla}
        onClose={() => setEditingPlantilla(null)}
      />
    </section>
  );
}

/**
 * Editor INLINE de plantillas (tanda ajustes-finales): edita en sitio las
 * mismas claves de installation.messages — misma fuente de verdad que la
 * pestaña Mensajes de /installation, sin duplicar nada.
 */
function PlantillaEditorModal({
  asistente,
  onClose,
}: {
  asistente: AssistantStatus | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const config = useQuery({
    queryKey: ['installation', 'config'],
    queryFn: installationApi.getInstallationConfig,
    enabled: Boolean(asistente),
  });
  const mensajes = (config.data?.messages ?? {}) as unknown as Record<string, string>;

  // Al abrir (o cambiar de asistente), los borradores arrancan del valor vigente.
  useEffect(() => {
    if (!asistente || !config.data) return;
    const iniciales: Record<string, string> = {};
    for (const key of asistente.plantillas) {
      iniciales[key] = ((config.data.messages as unknown as Record<string, string>)[key] ?? '');
    }
    setDrafts(iniciales);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asistente?.key, config.dataUpdatedAt]);

  const save = useMutation({
    mutationFn: () => {
      const c = config.data!;
      const messages = { ...(c.messages as unknown as Record<string, string>), ...drafts };
      return installationApi.saveInstallationConfig({
        businessName: c.businessName,
        agent: c.agent,
        catalog: c.catalog,
        notionDatabaseId: c.notionDatabaseId,
        crm: c.crm,
        messages: messages as unknown as typeof c.messages,
        slaHours: c.slaHours,
        currencyPrefix: c.currencyPrefix,
        // El glosario viaja tal cual lo cargó: esta pantalla solo edita una
        // plantilla y no puede dejar el vocabulario del negocio afuera del PUT.
        glosario: c.glosario,
        // Mismo criterio: sin esto el PUT de la plantilla pisaba los catálogos
        // de Trabajos del negocio.
        trabajos: c.trabajos,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['assistants'] });
      void qc.invalidateQueries({ queryKey: ['installation'] });
      toast.success('Plantilla guardada');
      onClose();
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : 'No se pudo guardar la plantilla'),
  });

  return (
    <Modal
      open={Boolean(asistente)}
      onClose={onClose}
      size="lg"
      title={`Plantilla: ${asistente?.nombre ?? ''}`}
      description="Es el mismo texto de Instalación → Mensajes (una sola fuente de verdad)."
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            onClick={() => save.mutate()}
            loading={save.isPending}
            disabled={!config.data}
          >
            Guardar
          </Button>
        </>
      }
    >
      {config.isLoading ? (
        <Spinner size={22} />
      ) : (
        <div className="space-y-4">
          {(asistente?.plantillas ?? []).map((key) => (
            <div key={key} className="space-y-1.5">
              {(asistente?.plantillas.length ?? 0) > 1 && (
                <p className="text-xs font-medium text-slate-500 dark:text-slate-400">{key}</p>
              )}
              <textarea
                rows={4}
                className="w-full rounded-md border border-surface-border bg-white p-2 text-sm dark:border-slate-700 dark:bg-slate-900"
                value={drafts[key] ?? mensajes[key] ?? ''}
                onChange={(e) => setDrafts((d) => ({ ...d, [key]: e.target.value }))}
              />
              {(asistente?.variables[key]?.length ?? 0) > 0 && (
                <p className="flex flex-wrap items-center gap-1 text-xs text-slate-400">
                  Variables:
                  {asistente!.variables[key]!.map((v) => (
                    <code
                      key={v}
                      className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] dark:bg-slate-800"
                    >
                      {`{{${v}}}`}
                    </code>
                  ))}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}
